<template>
    <div>
        <DataTable
            :value="groups"
            :lazy="true"
            :paginator="true"
            :rows="10"
            ref="dt"
            dataKey="id"
            :totalRecords="totalRecords"
            :loading="loading"
            @page="onPage($event)"
            responsiveLayout="scroll"
            v-model:filters="filters"
            :globalFilterFields="['code', 'description']"
            :rowsPerPageOptions="[5, 10, 20, 50, 100]"
            paginatorTemplate="CurrentPageReport JumpToPageInput FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
            currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries :: Page No:"
        >
            <template #empty> No Groups Available. </template>

            <Column field="id" header="SN" ref="id"> </Column>
            <Column field="code" header="CODE" ref="code"> </Column>
            <Column field="description" header="DESCRIPTION" ref="description"> </Column>
            <Column field="active" header="ACTIVE" ref="active">
                <template #body="slotProps">
                    <span class="p-column-title">ACTIVE</span>
                    <Tag :value="slotProps.data.active == 1 ? 'True' : 'False'" :severity="slotProps.data.active == 1 ? 'success' : 'danger'" :rounded="true"></Tag>
                </template>
            </Column>
            <Column field="permissions" header="PERMISSIONS" ref="permissions">
                <template #body="slotProps">
                    <span class="p-column-title">PERMISSIONS</span>
                    <Button :label="`${Object.keys(slotProps.data.permissions).length}`" icon="pi pi-tags" class="mr-2 mb-2" @click="toggleGroupPermissionTable($event, slotProps.data.permissions)"></Button>
                </template>
            </Column>
            <Column field="users_list" header="USERS" ref="group_users">
                <template #body="slotProps">
                    <span class="p-column-title">USERS</span>
                    <Button icon="pi pi-user" label="Users" class="m-2 p-button p-button-warning" v-tooltip="'Show Group Users'" @click="toggleGroupUsersTable($event, slotProps.data.id)"> </Button>
                </template>
            </Column>
            <Column field="created_at" header="CREATED AT" ref="created_at">
                <template #body="slotProps">
                    <span class="p-column-title">ACTIVE</span>
                    {{ formattedDate(slotProps.data.created_at) }}
                </template>
            </Column>

            <!-- v-if="checkAuthorized(allPermissions.WRITE_THEME)" -->
            <Column field="actions" header="ACTIONS" style="min-width: 8rem">
                <template #body="slotProps">
                    <Button icon="pi pi-pencil" class="p-button-rounded p-button-success mr-2" @click="editGroupDetails(slotProps.data)" />
                    <Button icon="pi pi-trash" class="p-button-rounded p-button-warning" @click="confirmDelete(slotProps.data)" />
                </template>
            </Column>
        </DataTable>

        <!-- Dialog for Permission lists -->
        <OverlayPanel ref="permissionDetails" :showCloseIcon="true" id="overlay_panel" style="width: 400px" :breakpoints="{ '960px': '75vw' }">
            <DataTable :value="groupPermissionList" :paginator="true" :rows="5" responsiveLayout="scroll">
                <Column field="code" header="Code"></Column>
                <Column field="description" header="Description"> </Column>
            </DataTable>
        </OverlayPanel>

        <!-- Dialog for Group Users lists -->
        <OverlayPanel ref="groupUsersDetails" :showCloseIcon="true" id="overlay_panel2" style="width: 400px" :breakpoints="{ '960px': '75vw', '640px': '150vw' }">
            <DataTable :value="groupUserList" :loading="usersLoading" :lazy="true" :paginator="true" :rows="5" responsiveLayout="scroll">
                <template #empty> No Users available </template>
                <Column field="id" header="ID" />
                <Column field="name" header="NAME" />
                <Column field="surname" header="SURNAME" />
            </DataTable>
        </OverlayPanel>

        <!-- Dialog to edit user groups -->

        <Dialog v-model:visible="editGroupDialog" :style="{ width: '85vw' }" header="Edit User Group" :modal="true" class="p-fluid dialog-header" :dismissableMask="true">
            <add-new-group v-show="editGroupDialog && editGroup" :editGroup="editGroup" @group-created="groupCreated" />
        </Dialog>
    </div>
</template>

<script>
import { FilterMatchMode } from 'primevue/api';
import AddNewGroup from './AddNewGroup.vue';
import GroupService from '../../../services/group-service';
import { formatDate } from '@/helpers/date-formatter';

export default {
    name: 'GroupsTable',
    components: { AddNewGroup },
    data() {
        return {
            loading: false,
            usersLoading: false,
            totalRecords: null,
            groups: null,
            lazyParams: {},
            filters: null,
            groupPermissionList: [],
            groupUserList: [],
            editGroup: {},
            editGroupDialog: false,
        };
    },
    props: {
        reloadGroupTable: {
            type: Boolean,
        },
        companyId: {
            type: Number,
            required: true,
        },
    },
    groupService: null,
    watch: {
        reloadGroupTable: {
            handler() {
                this.loadLazyData();
            },
        },
    },
    created() {
        this.groupService = new GroupService();
        this.initFilters();
    },
    mounted() {
        this.loading = true;
        this.lazyParams = {
            first: 0,
            rows: this.$refs.dt.rows,
        };
        this.loadLazyData();
    },
    methods: {
        capitalizeValue(val) {
            return val
                .toLowerCase()
                .split(' ')
                .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                .join(' ');
        },
        formattedDate(dateVal) {
            return formatDate(dateVal);
        },
        loadLazyData() {
            this.loading = true;
            setTimeout(() => {
                const bodyParams = {
                    page_size: this.lazyParams.rows,
                    page: this.lazyParams.page + 1,
                    company_id: this.companyId,
                    paginate: true,
                };
                this.groupService.getAllGroups(bodyParams).then((response) => {
                    this.groups = bodyParams.paginate ? response.data.data : response.data;
                    this.totalRecords = bodyParams.paginate ? response.data.total : response.data.length;
                    this.loading = false;
                });
            }, Math.random() * 1000 + 250);
        },
        initFilters() {
            this.filters = {
                global: { value: null, matchMode: FilterMatchMode.CONTAINS },
            };
        },
        clearFilter() {
            this.initFilters();
        },
        onPage(event) {
            this.lazyParams = event;
            this.loadLazyData();
        },
        toggleGroupPermissionTable(event, val) {
            this.groupPermissionList = val;
            this.$refs.permissionDetails.toggle(event);
        },
        toggleGroupUsersTable(event, groupId) {
            this.usersLoading = true;
            this.loadPermissionGroupUsers(groupId);
            this.$refs.groupUsersDetails.toggle(event);
        },
        async loadPermissionGroupUsers(groupId) {
            this.groupUserList = await this.groupService.getPermissionGroupUsers(groupId, this.companyId);
            this.usersLoading = false;
        },
        editGroupDetails(group) {
            this.editGroup = group;
            this.editGroupDialog = true;
        },
        confirmDelete(data) {
            // console.log(data);
            this.$confirm.require({
                message: `Are you sure you want to delete group ${data?.code}?`,
                header: 'Confirmation',
                icon: 'pi pi-exclamation-triangle',
                accept: () => {
                    this.groupService.deleteGroup(data?.id).then((res) => {
                        if (res.statusCode === 200) {
                            this.$toast.add({ severity: 'success', summary: 'Deleted', detail: 'Group has been deleted', life: 5000 });
                            this.loadLazyData();
                        }
                    });
                },
                reject: () => {
                    this.$toast.add({ severity: 'error', summary: 'Rejected', detail: 'Group not deleted', life: 5000 });
                },
            });
        },
        groupCreated() {
            this.editGroupDialog = false;
            this.loadLazyData();
        },
    },
};
</script>
